import { CancelToken } from 'axios';
import { getIn, connect, FieldArray } from 'formik';
import { get, throttle } from 'lodash';
import PropTypes from 'prop-types';
import { Fragment, useState, useRef, useMemo, useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { TextInput } from '@hivebrite/design-system/Inputs';

import { useDonationContext } from 'hb-react/frontoffice/contexts/DonationContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import SectionTitle from 'hb-react/shared/apps/Donations/components/SectionTitle';
import { getGiftIndex } from 'hb-react/shared/apps/Donations/utils/gifts';
import { dynamicDonationResourcePath } from 'hb-react/shared/apps/Donations/utils/whiteLabel';
import { Hidden } from 'hb-react/shared/components/formik/components/inputs';
import { CANCEL_FETCH_MESSAGE } from 'hb-react/shared/constants/cancelFetch';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import FundsList from './FundsList';
import GiftsList from './GiftsList';
import CustomFundHidden from '../CustomFundHidden';

const SearchIcon = () => (
  <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
);

const FundsSection = ({
  formik,
  campaign,
  setGiftAmount,
  setGiftCustomFund,
  setAmountsEqually,
  updateGlobalAmountFromGifts,
}) => {
  const [search, setSearch] = useState('');
  const [searchedFunds, setSearchedFunds] = useState([]);
  const giftsInputName = 'donation_order[gifts_attributes]';

  const giftsArrayRef = useRef();
  const requestCancelTokenRef = useRef(null);
  const { currentUser } = useDefaultAppContexts();
  const { whitelabelURL } = useDonationContext();
  const whitelabelURLEnabled = useMemo(
    () => (currentUser ? false : whitelabelURL),
    [currentUser, whitelabelURL],
  );
  // LOGIC

  const removeGift = (giftId) => {
    const gifts = getIn(formik.values, giftsInputName);
    const totalAmount = getIn(formik.values, 'donation_order[amount]');

    giftsArrayRef.current.remove(giftId);
    setAmountsEqually({ giftCount: gifts.length - 1, totalAmount });
  };

  const addGift = ({
    fundId,
    title,
    isFallback,
    paymentAccountExternalAccountId,
  }) => {
    const gifts = getIn(formik.values, giftsInputName);
    const totalAmount = getIn(formik.values, 'donation_order[amount]');

    giftsArrayRef.current.push({
      fund_id: fundId,
      amount: '',
      title,
      payment_account_external_account_id: paymentAccountExternalAccountId,
      is_fallback: isFallback,
    });
    setAmountsEqually({ giftCount: gifts.length + 1, totalAmount });
  };

  // HANDLERS

  const handleClickOnFund = ({
    id: fundId,
    title,
    is_fallback: isFallback,
    payment_account_external_account_id: paymentAccountExternalAccountId,
  }) => {
    const gifts = getIn(formik.values, giftsInputName);
    const giftIndex = getGiftIndex({ selectedGifts: gifts, fundId });

    if (giftIndex > -1) {
      removeGift(giftIndex);
    } else {
      addGift({
        fundId,
        title,
        isFallback,
        paymentAccountExternalAccountId,
      });
    }
  };

  const handleFundsSearch = (query) => {
    const trimQuery = query?.target?.value?.trim();

    if (requestCancelTokenRef.current) {
      requestCancelTokenRef.current.cancel(CANCEL_FETCH_MESSAGE);
    }

    requestCancelTokenRef.current = CancelToken.source();
    const { id } = campaign;
    const path = dynamicDonationResourcePath(
      Routes.funds_donation_path(id, { query: trimQuery }),
      {
        whitelabelURL: whitelabelURLEnabled,
      },
    );

    axios
      .get(path, { cancelToken: requestCancelTokenRef.current.token })
      .then(({ data }) => {
        setSearch(trimQuery);
        setSearchedFunds(get(data, 'funds'));
      })
      .catch((error) => {
        if (error.message === CANCEL_FETCH_MESSAGE) {
          return;
        }
        displayXhrError(error);
      });
  };
  const debouncedHandleFundsSearch = useCallback(
    throttle(handleFundsSearch, 700),
    [],
  );

  // RENDERERS

  const { funds = [], fund_categories: fundCategories = [] } = campaign;
  const gifts = getIn(formik.values, giftsInputName);
  const searchText = __('Search a specific fund');

  return (
    <div className="push--bottom" data-testid="funds-section">
      <div className="flex flex-end space-between palm--flex-wrap">
        <SectionTitle
          title={s__('Donations|Designation')}
          subTitle={s__('Donations|Choose your area(s) of impact')}
          mb={5}
        />
        <Box mb={5}>
          <TextInput
            ariaLabel={searchText}
            placeholder={searchText}
            id="funds_search"
            name="funds_search"
            icon={SearchIcon}
            onChange={debouncedHandleFundsSearch}
          />
        </Box>
      </div>

      <FundsList
        isSearching={!!search}
        funds={funds}
        searchedFunds={searchedFunds}
        categories={fundCategories}
        handleClickOnFund={handleClickOnFund}
      />
      <FieldArray name={giftsInputName}>
        {(giftsArray) => {
          giftsArrayRef.current = giftsArray;

          return gifts.map((gift, index) => (
            <Fragment key={index}>
              <Hidden name={`${giftsInputName}[${index}][fund_id]`} />
              <Hidden name={`${giftsInputName}[${index}][title]`} />
              <CustomFundHidden
                name={`${giftsInputName}[${index}][custom_fund]`}
                gift={gift}
              />
              <Hidden
                name={`${giftsInputName}[${index}][amount]`}
                validate={{
                  required: true,
                  minimum: 0,
                  maximumNumberOfDecimals: 2,
                }}
              />
            </Fragment>
          ));
        }}
      </FieldArray>
      <GiftsList
        campaign={campaign}
        onRemoveGift={removeGift}
        onChangeAmount={setGiftAmount}
        onChangeCustomFund={setGiftCustomFund}
        onSetAmountsEqually={setAmountsEqually}
        onUpdateGlobalAmountFromGifts={updateGlobalAmountFromGifts}
      />
    </div>
  );
};

FundsSection.propTypes = {
  campaign: PropTypes.shape({
    funds: PropTypes.array,
  }).isRequired,
  setGiftAmount: PropTypes.func.isRequired,
  setGiftCustomFund: PropTypes.func.isRequired,
  setAmountsEqually: PropTypes.func.isRequired,
  updateGlobalAmountFromGifts: PropTypes.func.isRequired,
};

export default connect(FundsSection);
